/**
 * Session management service for chat conversations
 */

export class SessionService {
  private static readonly SESSION_KEY = 'chat_session_id';
  private static sessionId: string = '';

  /**
   * Get or create a session ID for the current chat session
   * @returns Promise<string> - The session ID
   */
  static async getSessionId(): Promise<string> {
    // Return existing session ID if available
    if (this.sessionId) {
      return this.sessionId;
    }

    // Check if session ID exists in localStorage
    const storedSessionId = localStorage.getItem(this.SESSION_KEY);
    if (storedSessionId) {
      this.sessionId = storedSessionId;
      return this.sessionId;
    }

    // Generate new session ID from backend
    try {
      const response = await fetch('https://api-jurisai.polykloud.com/api/queries/session', {
        method: 'GET',
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to generate session ID: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && data.sessionId) {
        this.sessionId = data.sessionId;
        localStorage.setItem(this.SESSION_KEY, this.sessionId);
        return this.sessionId;
      } else {
        throw new Error('Invalid session response from server');
      }
    } catch (error) {
      console.error('Failed to generate session ID:', error);
      // Fallback to client-generated UUID
      this.sessionId = this.generateClientSessionId();
      localStorage.setItem(this.SESSION_KEY, this.sessionId);
      return this.sessionId;
    }
  }

  /**
   * Generate a new session ID (clears current session)
   * @returns Promise<string> - The new session ID
   */
  static async createNewSession(): Promise<string> {
    this.sessionId = '';
    localStorage.removeItem(this.SESSION_KEY);
    return this.getSessionId();
  }

  /**
   * Clear the current session
   */
  static clearSession(): void {
    this.sessionId = '';
    localStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Generate a client-side session ID as fallback
   * @returns string - A UUID-like session ID
   */
  private static generateClientSessionId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

export default SessionService;
